// Central balance constants. Every tunable number lives here (or in the
// per-entity config files) so balancing never means hunting through logic.
// The two headline curves come straight from the blueprint: production scales
// x1.1 per level, cost scales x1.15 per level, so progress self-slows.

export const BALANCE = {
  // ---- Headline growth curves ----
  productionGrowth: 1.1, // multiplier on output per building level
  costGrowth: 1.15, // multiplier on price per building level

  // ---- Harvest (cursor damage circle) ----
  harvest: {
    tickRate: 3, // damage ticks per second the circle deals (slower, less frantic
    // frequency: a node is worked down over a second or two, not vanished instantly)
    baseDamage: 5, // damage per tick (fewer, meatier hits; trimmed from 6 in the
    // macro-pacing pass so the early economy breathes instead of sprinting)
    baseRadius: 50, // horizontal px radius of the damage area before modifiers. The
    // area is an iso ground oval (height = radius/2) so it hugs the tiles and reads
    // small (about a tile and a half); it grows via Wide Reach skill / Wide Survey card
    critChance: 0.12, // chance a tick is a critical hit
    critMult: 2.5, // critical damage multiplier
    falloff: 0.35, // damage retained at the circle's edge (1 = no falloff)
    // Sait, 2026-10-20: a cut node grew back on the same tile seconds later, so
    // harvesting was a fixed loop over known spots. A depleted node now waits a
    // random multiple of its resource's base respawn and regrows on a random free
    // tile, so supply is scarcer and you have to look for it.
    respawnMultMin: 4,
    respawnMultMax: 8,
    respawnRingMin: 5, // Chebyshev ring for regrowth: off the building rings (1-4)
    respawnRingMax: 8,
  },

  // ---- Auto collection (workforce gathers without the cursor) ----
  autoCollect: {
    unlockPopulation: 3, // population needed before auto-collection switches on
    // Auto-collection is NOT available from the start: the settlement must first
    // develop its gathering infrastructure (this many lumber camp / quarry / mine
    // levels combined) before the workforce starts gathering on its own.
    unlockGatherers: 3,
    basePerWorker: 0.32, // resource/sec each assigned worker yields at tier 1
  },

  // ---- Population / workforce ----
  population: {
    base: 2, // population at the start of a fresh run
    growthPerSec: 0.09, // passive population growth per second (housing gated).
    // This paces the whole settlement: people arrive, then workplaces follow.
    // Trimmed from 0.12 in the macro-pacing pass; the game was peaking in ~5min.
    capBase: 4, // population cap before any houses
  },

  // ---- Auto building ----
  build: {
    minInterval: 2.0, // seconds between automatic building placements (deliberate pace)
    reserveFraction: 0.0, // fraction of a resource the builder will not spend
  },

  // ---- Age advancement ----
  ages: {
    // Age N is unlocked once the lifetime-gathered "progress" passes this.
    // progress = wood + stone + ore*1.5 + knowledge*3 accumulated this run.
    baseThreshold: 700, // slower first age so the card choice feels earned (raised
    // from 550 in the macro-pacing pass: the first card lands ~2min, not ~1.5)
    // NOTE (2026-07-30): discounting the FIRST threshold looks like the obvious
    // fix for "a new player never reaches a card", and it backfires. Advancing an
    // age raises the threat band (ageThreatGrowth 1.72), so arriving at age 1
    // early means the 150s opening raider hits for 14.6 instead of 8.5, and a
    // probe economy opener went from 100% wave-1 survival to 0%. The real cause
    // of the late first card was players not FINDING nodes (about two thirds of
    // taps hit empty grass); that is fixed in the render layer with node hints.
    // Do not reintroduce a first-age discount without re-running the probe.
    thresholdGrowth: 2.4, // each age costs notably more progress. Was 3.4: at that
    // value a casual saw only 2 cards per run and the synergy system was unreachable
    // before death; 2.4 lands 4-5 cards before the first boss (~85/170/270/400s).
    maxAge: 12, // Phase 2: more ages to climb (synergies make later picks matter)
    firstGateAge: 1, // ages BELOW this advance freely (crossing the threshold opens
    // the card directly, no boss). Onboarding: the first advance (age 0 -> 1) teaches
    // the beat and bootstraps the player with a card before the era gates begin, so
    // they never face a boss with an empty hand. Every age from here is boss-gated.
  },

  // ---- Waves / combat (era-gated model) ----
  // Threat is driven by the settlement's AGE/ERA, not the raw wave index. Within
  // an age, raider waves ramp gently (camping an age grinds you down); across
  // ages the whole threat band steps up. The era BOSS is the age gate: crossing
  // the progress threshold summons it, and only defeating it advances the age.
  waves: {
    firstWaveDelay: 150, // seconds before the first wave. Kept ABOVE the first free
    // age advance (~117s): with no cards yet even a base-threat raider overruns the
    // settlement, so the opening wave must land after the free onboarding card.
    interval: 52, // seconds between raider waves within an age
    warnLead: 5, // normal raider telegraph: enemies march in, clash, trade blows
    gateTelegraph: 9, // the era boss gathers at the gate for longer than a raider:
    // a distinct, dread-building event (dusk veil + "boss at the gate" banner)
    baseThreat: 17, // threat of an age-0 raider wave (wavesThisAge 0). A single
    // military pick clears it; passive defense alone sweats.
    ageThreatGrowth: 1.72, // threat multiplier PER AGE: each era band steps up, so
    // advancing without growing your defense is a trap. Paired with the building tax
    // below AND the spoils/tech economy nerf (cheap permanent defense techs were
    // trivializing this curve) so a skilled tech-buying run walls ~age 3-4 and needs
    // the skill tree. Tuned via scripts/probe.ts (1.66 + cheap techs let a competent
    // player cruise to age 4 / wave 26 with zero deaths — Sait, 2026-07-04).
    withinAgeRamp: 0.15, // each raider wave survived within the current age adds this
    // fraction to threat. Advancing an age resets it to 0. Sized so lingering in an
    // age (many raider waves before clearing its gate) genuinely grinds you down but
    // does not instantly death-spiral. Stops "26 waves, never in danger" runs.
    gateBossMult: 1.55, // era-boss spike over its age's raider band. A real spike but
    // not a pure card-RNG coin flip; the age curve and the economy nerf do most of
    // the walling.
    firstGateBossMult: 1.3, // ...except the FIRST gate (age = firstGateAge), which is
    // the first boss a player ever meets. Live analytics (2026-07-27): it killed 6 of
    // the last 8 runs, one player five times in a row, before the loop had taught them
    // anything. Softened from the 1.67 the ramp produced at age 1 so gate 1 teaches
    // the beat; every later gate keeps the full escalating multiplier.
    gateBossRamp: 0.12, // each later age gate adds this to the boss multiplier
    // (age-2 gate 1.62x, age-3 1.74x ...). Later gates are real achievements.
    gateBossMax: 2.1, // ramp cap (deep gate bosses stay brutal but bounded)
    // Threat also rises with settlement SIZE so a compounding defense (synergies,
    // techs, artifacts, skills) cannot permanently outscale the age curve. Age is
    // the base axis; the building tax is the ANTI-COMPOUNDING lever, sized to bite
    // the deep-run defense stack (which multiplies many bonuses) without touching
    // the onboarding waves.
    threatPerBuilding: 0.06, // each building past the free window adds this fraction.
    // Part of the late-game wall: a small settlement (early gates) is lightly taxed,
    // but a sprawling empire (late gates, many buildings) faces a compounding
    // multiplier that its multiplicative defense stack struggles to outrun.
    threatFreeBuildings: 24, // buildings below this add no development threat. The
    // auto-builder reaches ~14 buildings before wave 1, so the free window sits above
    // that: onboarding waves untaxed, but a 55-building empire faces ~2.9x threat.
    baseSpoils: 5, // wave currency for clearing an age-0 raider.
    spoilsAgeGrowth: 1.45, // spoils scale with the AGE band, like threat, and are
    // FLAT within an age. This is the anti-camping economy: lingering at a gate
    // earns linear spoils while the within-age threat ramp climbs, so farming the
    // gate cannot bank a full defense stack. (The old 1.14^rawWave growth re-flooded
    // the nerfed economy: ~460 spoils by wave 20 vs a ~164-spoils total tech sink.)
    // Below ageThreatGrowth on purpose: defense techs get relatively pricier deeper.
    rewardResourceFactor: 0.22, // resource reward as a fraction of current/sec (was
    // 0.35: wave wins also flooded resources, over-fuelling the building/defense engine)
    // ---- Onboarding grace ----
    // A brand-new player gets ONE card before the first wave and, if it is an
    // economy/knowledge pick, a base settlement's defense loses to a full-threat
    // raider (a silent 2-minute game over on 2 of the 3 opening picks). These
    // soften the FIRST few raider waves so any opening pick survives, then fade
    // to full threat. Gate bosses are NEVER softened, and by the time a skilled
    // run walls (age 2-5, many waves deep) the window is long closed, so the
    // tuned mid/late curve is untouched.
    onboardWaves: 3, // raider waves over which the softener fades to 1.0 (wave 4 = full)
    onboardThreatMult: 0.5, // wave-1 raider threat as a fraction of normal (fades to 1.0)
    // NOTE on send-wave-now (2026-07-28): live analytics show 76% of all waves
    // pulled in early, which looked like an income exploit (spoils are flat
    // within an age, so more waves per minute = more spoils per minute). Probing
    // it says otherwise: withinAgeRamp already prices the button, because a
    // masher stalls in a low age where spoils are small while a patient run
    // reaches ages that pay 1.45^age more. Measured with the rational-masher
    // strategy in probe.ts: mashing earns 0.72x a patient run's spoils and walls
    // at age 2 in 5/5 runs. So the skip stays FREE; do not "fix" it again
    // without re-running that comparison. The real signal in that 76% is that
    // the 52s interval feels too long, which is a pacing question, not a nerf.
  },

  // ---- Defense model ----
  // defense = (military axis + barracks power) * tech factor * run/meta mults.
  defense: {
    base: 9, // baseline defense of a fresh settlement. Low enough that passive
    // defense alone sweats every wave, high enough that picking a non-military
    // age-1 card is not a hidden 2-minute game over (economy/knowledge picks now
    // survive wave 1 ~93% instead of 0%). Military investment is still the lever.
    knowledgeTechFactor: 0.06, // tech bonus per sqrt(knowledge axis) point. Lowered so
    // knowledge is no longer a full substitute for a military investment (it helps,
    // but a knowledge-only build should sweat the waves). sqrt = diminishing returns.
  },

  // ---- Prestige (skill points on death) ----
  // Weighted toward AGES so pushing gates beats milking waves: with flat 1/wave
  // a defense-ahead player could camp before a gate and out-earn six age
  // advances in free ramp waves, the exact behavior withinAgeRamp punishes.
  // A push run (age 4, wave 10) still pays ~16: death stays a PAYDAY.
  prestige: {
    pointsPerAge: 3, // skill points for each age (era gate) reached
    pointsPerWaves: 0.4, // skill points per wave survived
    minPointsOnDeath: 3, // always earn at least this much (even a wave-1 death
    // buys something; a reward floor is what makes dying feel like the loop)
  },

  // ---- Planetary prestige (the big reset: conquer a planet, gain cosmic) ----
  planets: {
    conquestBaseAge: 6, // age (era-3 gate) to reach to conquer the first planet.
    // Reaching it means clearing 6 era gate bosses in one line: needs meta.
    conquestAgePerPlanet: 2, // each further planet needs this many more ages cleared
    threatGrowth: 1.8, // wave threat multiplier per planet (harder worlds)
    cosmicProductionPer: 0.12, // global production multiplier per cosmic point
    cosmicDefensePer: 0.1, // global defense multiplier per cosmic point
    cosmicAgeFactor: 0.2, // cosmic reward scales with the age reached
  },

  // ---- Calamities (random non-war disasters) ----
  // Civilizations do not only fall to armies: earthquakes, plagues, market
  // crashes and unrest strike on their own clock, fully at random. Gated past
  // the onboarding window so a brand-new settlement is never kicked while it
  // is still learning the game.
  calamities: {
    minWave: 4, // no calamity before this many waves are cleared
    minAge: 1, // ...and at least one age reached
    firstDelayMin: 240, // seconds from the gate opening to the first strike (rolled)
    firstDelayMax: 420,
    intervalMin: 200, // seconds between strikes (rolled)
    intervalMax: 380,
    retryDelay: 60, // gate not met when the timer fires: re-check this much later
    popFloor: 2, // population never drops below this
  },

  // ---- Offline progress ----
  offline: {
    baseCapHours: 8, // default offline accrual cap
    efficiency: 0.6, // offline production runs at this fraction of active
    minSecondsToReport: 60, // ignore tiny gaps (alt-tabs) below this
  },

  // ---- Settlement visual tiers (drives world evolution) ----
  // Indexed by cumulative building count thresholds.
  // Thresholds raised so a tier needs real population behind it (buildings are
  // population-gated): no more "village" with four people. Turkic names.
  settlementTiers: [
    { at: 0, name: 'Otağ' },
    { at: 6, name: 'Oba' },
    { at: 14, name: 'Köy' },
    { at: 28, name: 'Kasaba' },
    { at: 46, name: 'Şehir' },
    { at: 70, name: 'Kağanlık' },
  ],
} as const;

/** Generic geometric scaling helper used everywhere for level-based curves. */
export function scaleByLevel(base: number, growth: number, level: number): number {
  return base * Math.pow(growth, level);
}
